import React from 'react';
import { BRAND, STATUS_ORDER, PRIORITY_ORDER, STATUS_LABELS, PRIORITY_LABELS, CATEGORY_LABELS, SOURCE_LABELS, type TicketStatus, type TicketPriority } from './statuses';
import { RelativeTime } from './RelativeTime';
import { AssigneeDropdown } from './AssigneeDropdown';
import { TagEditor } from './TagEditor';
import type { TicketDetail, TicketEvent } from './ticketApi';

interface Props {
  ticket: TicketDetail;
  onUpdate: (payload: {
    status?: TicketStatus;
    priority?: TicketPriority;
    assigned_admin_id?: string | null;
    clear_assignee?: boolean;
    tags?: string[];
  }) => Promise<void>;
  savingField: string | null;
}

export const TicketDetailSidebar: React.FC<Props> = ({ ticket, onUpdate, savingField }) => {
  const cat = ticket.category ? CATEGORY_LABELS[ticket.category] : undefined;
  const src = SOURCE_LABELS[ticket.source];
  const linked = ticket.requester.linked_user_profile;

  return (
    <div
      style={{
        width: 320,
        flexShrink: 0,
        padding: '24px 24px 60px 24px',
        borderLeft: `1px solid ${BRAND.border}`,
        background: BRAND.surfaceAlt,
        overflowY: 'auto',
      }}
    >
      {/* Requester */}
      <SidebarSection title="Requester">
        <div style={{ fontSize: 15, fontWeight: 600, color: BRAND.text, marginBottom: 2 }}>
          {ticket.requester.name || ticket.requester.email || '—'}
        </div>
        {ticket.requester.email && (
          <div style={{ fontSize: 12, color: BRAND.textTertiary, marginBottom: 10 }}>
            {ticket.requester.email}
          </div>
        )}
        {linked && (
          <div
            style={{
              marginTop: 8,
              padding: '10px 12px',
              background: BRAND.surface,
              border: `1px solid ${BRAND.border}`,
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, color: BRAND.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              Linked user
            </div>
            <div style={{ fontSize: 12, color: BRAND.textSecondary, marginBottom: 2 }}>
              Status: <strong>{linked.status || 'unknown'}</strong>
            </div>
            {linked.created_at && (
              <div style={{ fontSize: 11, color: BRAND.textMuted }}>
                Joined <RelativeTime iso={linked.created_at} />
              </div>
            )}
            <a
              href={`/admin/users?search=${encodeURIComponent(linked.email || linked.id)}`}
              style={{
                display: 'inline-block',
                marginTop: 8,
                fontSize: 12,
                color: BRAND.green,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              View user profile →
            </a>
          </div>
        )}
      </SidebarSection>

      {/* Ticket fields */}
      <SidebarSection title="Ticket">
        <FieldLabel>Status</FieldLabel>
        <InlineSelect
          value={ticket.status}
          options={STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABELS[s] }))}
          onChange={(v) => onUpdate({ status: v as TicketStatus })}
          saving={savingField === 'status'}
        />

        <FieldLabel>Priority</FieldLabel>
        <InlineSelect
          value={ticket.priority}
          options={PRIORITY_ORDER.map((p) => ({ value: p, label: PRIORITY_LABELS[p] }))}
          onChange={(v) => onUpdate({ priority: v as TicketPriority })}
          saving={savingField === 'priority'}
        />

        <FieldLabel>Assignee</FieldLabel>
        <AssigneeDropdown
          currentAssigneeId={ticket.assigned_admin?.id || null}
          currentAssigneeName={ticket.assigned_admin?.name || null}
          onChange={(id) => {
            if (id === null) {
              onUpdate({ clear_assignee: true });
            } else {
              onUpdate({ assigned_admin_id: id });
            }
          }}
        />

        <FieldLabel style={{ marginTop: 14 }}>Tags</FieldLabel>
        <TagEditor
          tags={ticket.tags}
          onChange={(tags) => onUpdate({ tags })}
        />
      </SidebarSection>

      {/* Metadata */}
      <SidebarSection title="Details">
        <MetaRow
          label="Source"
          value={
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: 100,
                background: src?.bg,
                color: src?.text,
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {src?.label || ticket.source}
            </span>
          }
        />
        {cat && (
          <MetaRow
            label="Category"
            value={
              <span style={{ fontSize: 12, color: BRAND.textSecondary, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <cat.icon size={13} />{cat.label}
              </span>
            }
          />
        )}
        <MetaRow label="Created" value={<RelativeTime iso={ticket.created_at} />} />
        {ticket.first_response_at && (
          <MetaRow label="First response" value={<RelativeTime iso={ticket.first_response_at} />} />
        )}
        {ticket.last_requester_message_at && (
          <MetaRow
            label="Last from user"
            value={<RelativeTime iso={ticket.last_requester_message_at} />}
          />
        )}
        {ticket.last_agent_message_at && (
          <MetaRow
            label="Last from team"
            value={<RelativeTime iso={ticket.last_agent_message_at} />}
          />
        )}
        {ticket.solved_at && (
          <MetaRow label="Solved" value={<RelativeTime iso={ticket.solved_at} />} />
        )}
      </SidebarSection>

      {/* Error context (for app bugs) */}
      {ticket.error_context && Object.keys(ticket.error_context).length > 0 && (
        <SidebarSection title="Error context">
          <pre
            style={{
              margin: 0,
              padding: 10,
              background: BRAND.surface,
              border: `1px solid ${BRAND.border}`,
              borderRadius: 6,
              fontSize: 10,
              color: BRAND.textTertiary,
              overflowX: 'auto',
              maxHeight: 200,
              fontFamily: 'Menlo, Monaco, monospace',
              lineHeight: 1.5,
            }}
          >
            {JSON.stringify(ticket.error_context, null, 2)}
          </pre>
        </SidebarSection>
      )}

      {/* Activity */}
      <SidebarSection title="Activity">
        <ActivityFeed events={ticket.events} />
      </SidebarSection>

      {/* Agent analysis (reserved slot — rendered only if present) */}
      {ticket.agent_classification && (
        <SidebarSection title="Analysis">
          <div
            style={{
              padding: 12,
              background: '#F3F0FF',
              border: '1px solid #DDD6FE',
              borderRadius: 8,
              fontSize: 12,
              color: '#4C1D95',
            }}
          >
            <pre style={{ margin: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(ticket.agent_classification, null, 2)}
            </pre>
          </div>
        </SidebarSection>
      )}

      {/* Ticket ID reference */}
      <div style={{ fontSize: 10, color: BRAND.textMuted, marginTop: 20, fontFamily: 'Menlo, Monaco, monospace' }}>
        id: {ticket.id}
      </div>
    </div>
  );
};

// ── Sub-components ───────────────────────────────────────────────────────────

const SidebarSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: BRAND.textTertiary,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: 12,
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const FieldLabel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 600,
      color: BRAND.textMuted,
      marginBottom: 5,
      marginTop: 12,
      ...style,
    }}
  >
    {children}
  </div>
);

const MetaRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '6px 0',
      fontSize: 12,
      borderBottom: `1px solid ${BRAND.borderSoft}`,
    }}
  >
    <span style={{ color: BRAND.textMuted }}>{label}</span>
    <span style={{ color: BRAND.textSecondary, fontWeight: 500 }}>{value}</span>
  </div>
);

const InlineSelect: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  saving: boolean;
}> = ({ value, options, onChange, saving }) => (
  <div style={{ position: 'relative' }}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={saving}
      style={{
        width: '100%',
        padding: '8px 28px 8px 12px',
        background: BRAND.surface,
        border: `1px solid ${BRAND.border}`,
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 500,
        color: BRAND.textSecondary,
        cursor: saving ? 'wait' : 'pointer',
        appearance: 'none',
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    <span
      style={{
        position: 'absolute',
        right: 10,
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        color: BRAND.textMuted,
        fontSize: 10,
      }}
    >
      {saving ? '…' : '▾'}
    </span>
  </div>
);

const ActivityFeed: React.FC<{ events: TicketEvent[] }> = ({ events }) => {
  if (!events || events.length === 0) {
    return <div style={{ fontSize: 12, color: BRAND.textMuted }}>No activity yet</div>;
  }

  return (
    <div style={{ fontSize: 11, lineHeight: 1.5 }}>
      {events.slice(0, 40).map((e) => (
        <div
          key={e.id}
          style={{
            padding: '6px 0',
            borderBottom: `1px solid ${BRAND.borderSoft}`,
            color: BRAND.textTertiary,
          }}
        >
          <div style={{ fontWeight: 600, color: BRAND.textSecondary }}>
            {describeEvent(e)}
          </div>
          <div style={{ color: BRAND.textMuted, fontSize: 10, marginTop: 1 }}>
            {e.admin_name && <>by {e.admin_name} · </>}
            <RelativeTime iso={e.created_at} />
          </div>
        </div>
      ))}
    </div>
  );
};

function describeEvent(e: TicketEvent): string {
  const nv = e.new_value || {};
  const ov = e.old_value || {};
  switch (e.event_type) {
    case 'ticket_created':
      return `Ticket created (${(nv as any).source || 'unknown'})`;
    case 'status_change':
      return `Status: ${(ov as any).status} → ${(nv as any).status}`;
    case 'priority_change':
      return `Priority: ${(ov as any).priority} → ${(nv as any).priority}`;
    case 'assigned':
      return `Assigned`;
    case 'unassigned':
      return `Unassigned`;
    case 'tags_updated':
      return `Tags updated`;
    case 'reply_sent':
      return `Reply sent to requester`;
    case 'note_added':
      return `Internal note added`;
    case 'inbound_reply':
      return `Inbound reply from requester`;
    case 'reopened':
      return `Ticket reopened`;
    default:
      return e.event_type;
  }
}
